import type { SlideLayoutLocationState } from '@gripp/shared-ui';
import { SlideAction, SlideLayout, ViewMode } from '@gripp/shared-ui';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { RoutineSubmitSource } from '../assetUtils';
import { AddButton } from '../../shared/components/addButton/addButton';
import { RoutineAllColumn } from './asset-routines-all-column';
import { AssetRoutinesHeader } from './asset-routines-header';
import { AssetRoutineRearrangeColumn } from './asset-routines-rearrange-column';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

type AssetRoutinesRearrangeLocationState = SlideLayoutLocationState & {
  from?: RoutineSubmitSource;
  routinePanelReturnPath?: string;
};

export const AssetRoutinesRearrange = () => {
  const { assetId, issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [requestRoutineVisible, setRequestRoutineVisible] = useState(false);
  const locationState =
    location.state as AssetRoutinesRearrangeLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const cameFromRoutinesList =
    locationState?.from === RoutineSubmitSource.RoutinesList;
  const routinePanelReturnPath = locationState?.routinePanelReturnPath;

  if (!assetId) {
    return null;
  }

  const assetCardColumn = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: issueId
            ? `/activities/${issueId}/asset/${assetId}`
            : `/assets/${assetId}`,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate(issueId ? '/activities' : '/assets'),
    disableScroll: true,
  };

  const routinesListColumn = {
    title: 'assets.routines',
    mainComponent: <RoutineAllColumn assetId={assetId} />,
    headerComponent: (
      <AssetRoutinesHeader assetId={assetId} issueId={issueId} />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    backButtonTransitionExitColumn: 'both' as const,
    onBackButtonPress: () =>
      navigate(issueId ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: true,
  };

  const leftColumn = cameFromRoutinesList
    ? routinesListColumn
    : assetCardColumn;

  const rightColumn = {
    title: 'topNav.edit',
    mainComponent: (
      <AssetRoutineRearrangeColumn
        assetId={assetId}
        requestRoutineVisible={requestRoutineVisible}
        onRequestRoutineClose={() => setRequestRoutineVisible(false)}
      />
    ),
    headerComponent: (
      <AddButton onPress={() => setRequestRoutineVisible(true)} />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: cameFromRoutinesList
      ? ('concurrent' as const)
      : undefined,
    onBackButtonPress: () => {
      if (cameFromRoutinesList) {
        if (routinePanelReturnPath) {
          navigate(routinePanelReturnPath, {
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          });
        } else {
          navigate('../all', {
            relative: 'path',
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          });
        }
        return;
      }

      navigate('../..', { relative: 'path' });
    },
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
